import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaHeart, FaTrash } from "react-icons/fa";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import "./Cart.css";
import { useLanguage } from "../../contexts/LanguageContext";

export default function Cart() {
  const navigate = useNavigate();
  const { storeId } = useParams();
  const { translations: tRaw } = useLanguage();
  const t = { ...tRaw, cartPage: { ...tRaw?.cartPage } };
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeData, setStoreData] = useState(null);

  useEffect(() => {
    // Load cart items from localStorage
    const loadCartItems = async () => {
      try {
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          const parsedCart = JSON.parse(savedCart);
          setCartItems(parsedCart);
          
          // If we have storeId, fetch store data
          if (storeId) {
            try {
              const userRef = doc(db, "users", storeId);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                setStoreData(userSnap.data());
              }
            } catch (error) {
              console.error('Error fetching store data:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error loading cart:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCartItems();
  }, [storeId]);

  const updateCartInStorage = (newCart) => {
    localStorage.setItem('cart', JSON.stringify(newCart));
    setCartItems(newCart);
  };

  const handleQuantityChange = (productId, change) => {
    const updatedCart = cartItems.map(item => {
      if (item.id === productId) {
        const newQuantity = item.quantity + change;
        if (newQuantity <= 0) {
          return null; // Remove item if quantity becomes 0
        }
        return { ...item, quantity: newQuantity };
      }
      return item;
    }).filter(Boolean); // Remove null items

    updateCartInStorage(updatedCart);
  };

  const removeFromCart = (productId) => {
    const updatedCart = cartItems.filter(item => item.id !== productId);
    updateCartInStorage(updatedCart);
  };

  const toggleLike = (productId) => {
    const updatedCart = cartItems.map(item => {
      if (item.id === productId) {
        return { ...item, isLiked: !item.isLiked };
      }
      return item;
    });
    updateCartInStorage(updatedCart);
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.SalePrice || item.Price;
      return total + (price * item.quantity);
    }, 0);
  };

  const handleContinueShopping = () => {
    if (storeId) {
      navigate(`/products/${storeId}`);
    } else {
      navigate(-1);
    }
  };

  const handleOrder = () => {
    // Implement order functionality
    console.log('Order placed:', cartItems);
    alert('Order placed successfully!');
    // Clear cart after successful order
    updateCartInStorage([]);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-animation"></div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      {/* Header */}
      <div className="cart-header">
        <button className="cart-back-button" onClick={handleBack}>
          <FaArrowLeft />
        </button>
        <h1 className="cart-title">{t.cartPage?.title || "Shopping List"}</h1>
      </div>

      {cartItems.length === 0 ? (
        /* Empty Cart State */
        <div className="empty-cart-container">
          <div className="empty-cart-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
            </svg>
          </div>
          <h2 className="empty-cart-title">{t.cartPage?.emptyTitle || "Your cart is empty"}</h2>
          <p className="empty-cart-subtitle">{t.cartPage?.emptySubtitle || "Add some products to get started!"}</p>
          <button className="continue-shopping-btn" onClick={handleContinueShopping}>
            {t.cartPage?.continueShopping || "Continue Shopping"}
          </button>
        </div>
      ) : (
        /* Cart with Items */
        <div className="cart-content">
          {/* Total Price */}
          <div className="cart-total">
            ${calculateTotal().toFixed(2)}
          </div>

          {/* Store Section */}
          {cartItems.length > 0 && (
            <div className="cart-store-section">
              <div className="cart-store-info">
                <div className="cart-store-avatar">
                  {storeData ? (
                    <img
                      src={storeData.compressedOrganizationLogo || storeData.organizationLogo || storeData.profileImage || "/default-avatar.png"}
                      alt={storeData.organizationName || storeData.name || "Store"}
                      onError={(e) => {
                        e.currentTarget.src = "/default-avatar.png";
                      }}
                    />
                  ) : (
                    <span>T</span>
                  )}
                </div>
                <span className="cart-store-name">
                  {storeData ? (storeData.organizationName || storeData.name || "Store") : "Thinking Up"}
                </span>
              </div>
              <button className="cart-store-like-btn">
                <FaHeart />
              </button>
            </div>
          )}

          {/* Cart Items */}
          <div className="cart-items">
            {cartItems.map((item) => (
              <div key={item.id} className="cart-item">
                <div className="cart-item-checkbox">
                  <input type="checkbox" defaultChecked />
                </div>
                
                <div className="cart-item-image">
                  <img
                    src={item.Images?.[0] || item.Image}
                    alt={item.Title || item.ArabicTitle}
                    onError={(e) => {
                      e.currentTarget.src = "/default-product.png";
                    }}
                  />
                </div>
                
                <div className="cart-item-info">
                  <h3 className="cart-item-title">{item.Title || item.ArabicTitle}</h3>
                  <p className="cart-item-price">${Number((item.SalePrice ?? item.Price) ?? 0).toFixed(2)}</p>
                </div>
                
                <div className="cart-item-actions">
                  <div className="quantity-selector">
                    <button
                      className="quantity-btn minus"
                      onClick={() => handleQuantityChange(item.id, -1)}
                    >
                      -
                    </button>
                    <span className="quantity-value">{item.quantity}</span>
                    <button
                      className="quantity-btn plus"
                      onClick={() => handleQuantityChange(item.id, 1)}
                    >
                      +
                    </button>
                  </div>
                  
                  <button
                    className="remove-item-btn"
                    onClick={() => removeFromCart(item.id)}
                    title="Remove item"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Order Button */}
          <div className="cart-order-section">
            <div className="cart-final-total">
              ${calculateTotal().toFixed(2)}
            </div>
            <button className="order-btn" onClick={handleOrder}>
              {t.cartPage?.order || "Order"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
